import React, { useEffect, useContext, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppContext } from '../util/AppContext';
import useComponentVisible from '../hooks/useComponentVisible';
import DashboardFilterDropdown from '../components/dashboard/DashboardFilterDropdown';
import DashboardItemRow from '../components/dashboard/DashboardItemRow';
import { getConfigValue, isBatchPrintEnabledForAnyProduct } from '../util/getConfigValue';
import { formatSellSheetDate } from '../util/prettyDate';
import { filterByValue } from '../util/filterByValue';
import PreviewModal from '../components/PreviewModal';
import ToastAlert from '../components/ToastAlert';
import { getFileNameFromHeader } from '../util/getFileNameFromHeader';
import { Pagination } from '../components/Pagination';
import Dropdown from 'react-bootstrap/Dropdown';
import { useErrorBoundary } from 'react-error-boundary';
import { trackDocumentRequest, trackingEventType } from '../API/documentGenerate';
import { focusTypeName } from '../util/constants';
import { useRoutingValidation } from '../util/useRoutingValidation';
import { useBreadcrumbs } from '../util/BreadcrumbContext';
import PageTitle from '../components/PageTitle';
import PageContextMenu from '../components/PageContextMenu';

function RecentSalesAidsPage() {
    const { showBoundary } = useErrorBoundary();
    const [appState, setAppState] = useContext(AppContext);
    const [sellSheets, setSellSheets] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const [dashboardConfig, setDashboardConfig] = useState({});
    const [filterMenuRef, filterMenuIsVisible, setFilterMenuIsVisible, filterContentRef] = useComponentVisible(false);
    const professionalPrintingEnabled = getConfigValue(appState?.config?.ProfessionalPrinting?.Enabled, appState)?.Value === 'true' || false;
    const downLoadEnabled = getConfigValue(appState.config?.Generate?.Document?.Download?.Enabled, appState)?.Value === 'true' || false;
    const printEnabled = getConfigValue(appState.config?.Generate?.Document?.Print?.Enabled, appState)?.Value === 'true' || false;
    const batchPrintEnabled = isBatchPrintEnabledForAnyProduct(appState);
    const salesAidGenerationEnabled = getConfigValue(appState?.config?.Sellsheet?.Generation?.Enabled, appState)?.Value === 'true' || false;
    const navigate = useNavigate();

    const [defaultFilterState, setDefaultFilterState] = useState({
        current: true,
        archive: true,
        activeHistory: 3,
        previousHistory: 0,
        submitted: false,
        delayed: false,
        processing: false,
        cancelled: false
    });

    const [filterState, setFilterState] = useState(defaultFilterState);
    const [filteredSellSheets, setFilteredSellSheets] = useState([]);
    const [noResultsFound, setNoResultsFound] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [pdfFileName, setPdfFileName] = useState('');
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewModalSellSheet, setPreviewModalSellSheet] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [toastTitle] = useState('');
    const [toastMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [numberOfPages, setNumberOfPages] = useState(0);
    const [currentRecords, setCurrentRecords] = useState([]);
    const [quickSheetDownLoadEnabled, setQuickSheetDownLoadEnabled] = useState((getConfigValue(appState?.config?.QuickSheets?.Download?.Enabled, appState)?.Value || 'false') === 'true');
    const [systemDateFormat, setSystemDateFormat] = useState(getConfigValue(appState?.config?.System?.DateTime?.Format, appState)?.Value || null);
    const { setBreadcrumbs } = useBreadcrumbs();

    useEffect(() => {
        setBreadcrumbs([
            { label: getConfigValue(appState?.config?.Dashboard?.Header, appState)?.Value, path: "/RecentSalesAidsPage" }
        ]);
    }, [setBreadcrumbs]);

    useRoutingValidation();

    useEffect(() => {
        if (appState.configLoaded && appState.configRefreshed) {
            setDashboardConfig(getConfigValue(appState.config.Dashboard, appState));
            setSystemDateFormat(getConfigValue(appState?.config?.System?.DateTime?.Format, appState)?.Value || null);
            setQuickSheetDownLoadEnabled((getConfigValue(appState?.config?.QuickSheets?.Download?.Enabled, appState)?.Value || 'false') === 'true');
            setDefaultFilterState({
                current: true,
                archive: true,
                activeHistory: parseInt(getConfigValue(appState?.config?.Dashboard?.Filter?.Months?.Default, appState)?.Value) || 3,
                previousHistory: 0,
                submitted: false,
                delayed: false,
                processing: false,
                cancelled: false
            });
            setAppState({
                ...appState,
                prescriber: {},
                products: [],
                currentProduct: "",
                plans: [],
                category: "",
                mainMessage: "",
            })
        }
    }, [appState.configLoaded, appState.configRefreshed]);

    useEffect(() => {
        if (!dashboardConfig.SellSheetTable || dashboardConfig.SellSheetTable.Columns.length === 0) {
            return;
        }
    }, [dashboardConfig]);

    const getSellSheets = useCallback(function () {
        let url = `${process.env.REACT_APP_API_URL}SellSheet?maxMonths=${filterState.activeHistory}`;

        if (!filterState.activeHistory) {
            url = `${process.env.REACT_APP_API_URL}SellSheet`;
        }

        const fetchDashboard = async () => {
            setIsLoading(true);
            await fetch(url, {
                method: 'GET',
                credentials: 'include',
            })
                .then((response) => response.json())
                .then((responseJson) => {
                    setSellSheets(responseJson.rows.map((sellSheet) => formatSellSheetDate(sellSheet, systemDateFormat)));
                    setHeaders(responseJson.headers);
                    setIsLoading(false);
                })
                .catch((err) => {
                    showBoundary(err);
                });
        };
        fetchDashboard();
    }, [filterState.activeHistory, systemDateFormat]);

    //Get dashboard sellSheets
    useEffect(() => {
        // only fetch if a user is logged in
        if (!appState.user && !appState.configLoaded) {
            return;
        }

        getSellSheets();
    }, [appState.user, appState.configLoaded, filterState.activeHistory, getSellSheets]);

    const filterSellSheet = useCallback(function (element) {
        let shouldInclude = false;
        if ((filterState.current === true && element.data.active === true) || (filterState.archive === true && element.data.active === false)) {
            shouldInclude = true;

            if ((filterState.submitted === true || filterState.processing === true || filterState.delayed === true || filterState.cancelled === true) && element.data.printJobStatus === null) {
                // if the print job status is null and we have opted for a filter it cannot be included.
                shouldInclude = false;
            }
            else if (filterState.submitted === true || filterState.processing === true || filterState.delayed === true || filterState.cancelled === true) {
                // print job status is not null and one or more statuses are selected
                shouldInclude = (filterState.submitted === true && element.data.printJobStatus === 2)
                    || (filterState.processing === true && (element.data.printJobStatus === 0 || element.data.printJobStatus === 1))
                    || (filterState.delayed === true && element.data.printJobStatus === -1)
                    || (filterState.cancelled === true && element.data.printJobStatus === 3);
            }
        }
        return shouldInclude;
    }, [filterState]);

    useEffect(() => {
        if (!appState.configLoaded) {
            return;
        }

        //first apply the filters to reduce the search space, then apply the search term if defined
        let results = sellSheets.filter(filterSellSheet);
        if (searchInput.length > 0) {
            handleHcpSearch(results);
            results = filterByValue(results, searchInput.trim(), [], true);
        }
        setFilteredSellSheets(results);
        if (!results.length) {
            setNoResultsFound(true);
        } else {
            setNoResultsFound(false);
        }
        const pageSize = parseInt(dashboardConfig.RecordsPerPage?.Value, 10);
        let pages = Math.floor(results.length / pageSize);
        pages = results.length % pageSize > 0 ? pages + 1 : pages;
        setNumberOfPages(pages);
    }, [sellSheets, filterState, searchInput, currentPage, dashboardConfig, appState.configLoaded, filterSellSheet]);

    useEffect(() => {
        const pageSize = parseInt(dashboardConfig.RecordsPerPage?.Value, 10);
        const indexOfLastRecord = (currentPage + 1) * pageSize;
        const indexOfFirstRecord = indexOfLastRecord - pageSize;
        const currentRecords = filteredSellSheets.slice(indexOfFirstRecord, indexOfLastRecord);
        setCurrentRecords(currentRecords);
    }, [filteredSellSheets, currentPage]);

    useEffect(() => {
        if (isLoading) {
            setFeedbackMessage(getConfigValue(appState.config.Common?.Loading, appState)?.Value);
        } else if (noResultsFound) {
            setFeedbackMessage(getConfigValue(appState.config.Common?.NoResultsFound, appState)?.Value);
        } else {
            setFeedbackMessage('');
        }
    }, [isLoading, noResultsFound, appState]);

    function handleHcpSearch(results) {
        for (const r of results) {
            if (!r.data.hcps) {
                continue;
            }
            let index = 0;
            for (const hcp of r.data.hcps) {
                r.columns['hcp_name_' + index] = hcp.providerName;
                r.columns['hcp_zip_' + index] = hcp.zipCode;
                index++;
            }
        }
    }

    function handleChange(event) {
        setCurrentPage(0);
        setSearchInput(event.target.value);
    }

    function handleFilterChange(newFilter) {
        setCurrentPage(0);
        setFilterState(newFilter);
    }

    async function onGetPDF(id, documentGenerationProperties) {
        if (!id) {
            return;
        }

        let pdfFileName = '';
        let pdfUrl = '';

        await fetch(`${process.env.REACT_APP_API_URL}SellSheet/Download/${id}`, {
            method: 'GET',
            credentials: 'include',
        })
            .then((response) => {
                if (response.status !== 200) {
                    throw response.statusText;
                }

                let fileName = getFileNameFromHeader(response);
                pdfFileName = fileName;

                // store the properties of the last document generated
                setAppState({
                    ...appState,
                    lastGeneratedDocument: documentGenerationProperties,
                    sellSheetId: id,
                });

                return response.blob();
            })
            .then((blob) => {
                setIsLoading(false);
                const url = URL.createObjectURL(blob);
                pdfUrl = url;
            })
            .catch((err) => {
                setShowPreviewModal(false);
                setIsLoading(false);
                showBoundary(err);
            });

        return { pdfFileName, pdfUrl };
    }

    async function onAccessClick(id, documentGenerationProperties, sellSheet) {
        if (!id) {
            return;
        }

        let onGetPDFResult = await onGetPDF(id, documentGenerationProperties);

        if (onGetPDFResult?.pdfUrl) {
            setPdfFileName(onGetPDFResult.pdfFileName);
            setPdfUrl(onGetPDFResult.pdfUrl);
            setShowPreviewModal(true);
            setPreviewModalSellSheet(sellSheet);
        }
    }

    async function onPrintClick(id, documentGenerationProperties) {
        if (!id) {
            return;
        }

        let onGetPDFResult = await onGetPDF(id, documentGenerationProperties);

        if (onGetPDFResult?.pdfUrl) {
            try {
                // track print request
                trackDocumentRequest(trackingEventType.Print, documentGenerationProperties, id);

                window.open(onGetPDFResult.pdfUrl, 'PRINT');
            } catch (e) {
                showBoundary(e);
            }
        }
    }

    async function onDownloadClick(id, documentGenerationProperties) {
        if (!id) {
            return;
        }

        let onGetPDFResult = await onGetPDF(id, documentGenerationProperties);

        if (onGetPDFResult?.pdfUrl) {
            try {
                // track print request
                trackDocumentRequest(trackingEventType.DownLoad, documentGenerationProperties, id);

                const link = document.createElement('a');
                link.href = onGetPDFResult.pdfUrl;
                link.download = onGetPDFResult.pdfFileName;
                link.click();
            } catch (e) {
                showBoundary(e);
            }
        }
    }

    async function onCreateProfessionalPrintingDocumentClick(id, documentGenerationProperties) {
        if (!id) {
            return;
        }

        setIsLoading(true);
        let onGetPDFResult = await onGetPDF(id, documentGenerationProperties);

        if (onGetPDFResult?.pdfUrl) {
            try {
                navigate('/createProfessionalPrintingDocument', { state: { pdfURL: onGetPDFResult.pdfFileName, fileName: onGetPDFResult.pdfFileName, returnURL: '/RecentSalesAidsPage', isQuickSheet: false} });
            } catch (e) {
                showBoundary(e);
            }
        }
    }

    function pageChange(pageNumber) {
        setCurrentPage(pageNumber);
    }

    return (
        <div>
            <div className='container-xl'>
                <div className='row'>
                    <div className='d-flex justify-content-between align-items-center mb-3 mt-3 mt-4'>
                        <PageTitle title={dashboardConfig.Header?.Value} />
                    </div>
                </div>
            </div>
            <div className='container-xl mt-3 mt-md-4'>
                <div className='row my-3 mb-lg-4'>
                    <div className="col-12 mb-3 mb-md-0 col-md">
                        <div className='input-group'>
                            <input
                                value={searchInput}
                                type='text'
                                className='form-control icon-search'
                                id='dashboard-search'
                                placeholder='Search'
                                onChange={handleChange}></input>
                        </div>
                    </div>
                    <div className="col-12 col-md-4 col-lg-3 ps-md-0 d-flex">
                        <div className="dropdown flex-fill d-flex">
                            <button
                                ref={filterMenuRef}
                                type='button'
                                className='btn btn-outline-primary btn-icon w-100 me-2'
                                onClick={() => setFilterMenuIsVisible(!filterMenuIsVisible)}
                                data-bs-toggle='collapse'
                                data-bs-target='#options-row-1'
                                aria-expanded='false'
                                aria-controls='options-row-1'>
                                {dashboardConfig.FiltersButton?.Value}
                                <span className='fa-regular fa-chevron-down btn-icon'></span>
                            </button>
                            {filterMenuIsVisible && (
                                <DashboardFilterDropdown filterContentRef={filterContentRef} setFilterState={handleFilterChange} filterState={filterState} defaultFilterState={defaultFilterState} />
                            )}
                        </div>
                        {(salesAidGenerationEnabled || quickSheetDownLoadEnabled) &&
                            <PageContextMenu includeExportOption={false} forceShowSalesAidOption={true} />
                        }
                    </div>
                </div>
                <hr className='my-4 d-lg-none'></hr>
                <div className='container-xl'>
                    {!feedbackMessage &&
                        currentRecords.map((sellSheet) => {
                            return <DashboardItemRow
                                key={sellSheet.data.id}
                                headers={headers}
                                sellSheet={sellSheet}
                                onAccessClick={onAccessClick}
                                onPrintClick={onPrintClick}
                                onDownloadClick={onDownloadClick}
                                onCreateProfessionalPrintingDocumentClick={onCreateProfessionalPrintingDocumentClick}
                                canShowStatus={true}
                                professionalPrintingEnabled={professionalPrintingEnabled}
                                downLoadEnabled={downLoadEnabled}
                                printEnabled={printEnabled} />;
                        })}

                    {feedbackMessage ? (
                        <div className='row pd-plan-table-row pd-plan-table-card align-items-center'>
                            <div className='color-grey-d1 py-4'>{feedbackMessage}</div>
                        </div>
                    ) : null}
                </div>
            </div>
            {numberOfPages > 1 && <Pagination numberOfPages={numberOfPages} currentPage={currentPage} setCurrentPage={pageChange} />}

            <PreviewModal
                show={showPreviewModal}
                setShow={setShowPreviewModal}
                pdfUrl={pdfUrl}
                isLoading={isLoading}
                showGenerateBack={false}
                showPrintDownload={true}
                onGenerateClick={null}
                isValid={true}
                pdfFileName={pdfFileName}
                supportsProfessionalPrinting={true}
                provider={previewModalSellSheet?.data?.provider}
                territory={previewModalSellSheet?.data?.territory}
                sellSheetData={previewModalSellSheet?.data}
                returnURL={'/RecentSalesAidsPage'}
            />

            <ToastAlert show={showToast} setShow={setShowToast} title={toastTitle} message={toastMessage} />
        </div>
    );
}

export default RecentSalesAidsPage;
